import React from "react";
import {
  Box,
  Typography,
  Stack,
  useTheme,
  SvgIconProps,
  Paper,
  useMediaQuery,
} from "@mui/material";

interface PageHeaderProps {
  /**
   * Título principal da página
   */
  title: string;

  /**
   * Ícone opcional (MUI SvgIcon ou imagem custom, ex.: cannabis).
   */
  icon?: React.ComponentType<Partial<SvgIconProps>>;

  /**
   * Descrição opcional da página
   */
  description?: string;

  /**
   * Componente opcional para ser renderizado à direita (geralmente botões de ação)
   */
  action?: React.ReactNode;

  /**
   * Define se o título e descrição devem ser centralizados
   * @default false
   */
  centered?: boolean;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  action,
  icon: Icon,
  centered = false,
}) => {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.up("md"));
  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        maxWidth: "100%",
        borderRadius: 1,
        pt: 2,
        pb: 2,
        px: 2,
      }}
    >
      <Stack
        direction={isMediumScreen ? "row" : "column"}
        justifyContent={centered ? "center" : "space-between"}
        alignItems={
          centered ? "center" : isMediumScreen ? "flex-start" : "stretch"
        }
        spacing={isMediumScreen ? 2 : 1.5}
      >
        <Box
          flex={centered ? "unset" : 1}
          sx={{ textAlign: centered ? "center" : "left" }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent={centered ? "center" : "flex-start"}
          >
            {Icon && (
              <Icon
                sx={{
                  fontSize: 32,
                  color: "#00A884",
                }}
              />
            )}
            <Typography variant="h5" fontWeight={500}>
              {title}
            </Typography>
          </Stack>

          {description && (
            <Typography
              variant="body1"
              color="neutral600.main"
              sx={{ mt: 0.5, textAlign: centered ? "center" : "left" }}
            >
              {description}
            </Typography>
          )}
        </Box>

        {!centered && action && (
          <Box
            sx={{
              width: isMediumScreen ? "auto" : "100%",
              display: "flex",
              justifyContent: isMediumScreen ? "flex-end" : "flex-start",
              mt: isMediumScreen ? 0 : 2,
            }}
          >
            {action}
          </Box>
        )}
      </Stack>
    </Paper>
  );
};
